import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Spinner,
  Table,
} from "reactstrap";
import cogoToast from "cogo-toast";
import Swal from "sweetalert2";
import { FaEdit, FaEye, FaFileExcel, FaPlus, FaSearch, FaSync, FaTrash } from "react-icons/fa";
import {
  deleteWashProjectApi,
  listWashProjectsApi,
} from "../../../services/Api";
import { SUCCESS_RESULT } from "../../../Constants";
import {
  formatCell,
  formatMoney,
  washStatusColor,
  washStatusLabel,
  canCreateWashProject,
  canUpdateWashProject,
  canDeleteWashProject,
} from "./washProjectHelpers";
import "./WashProjects.css";

const EMPTY_FILTERS = {
  region: "",
  district: "",
  donor: "",
  status: "",
};

const WashProjectsList = () => {
  const history = useHistory();
  const canCreate = canCreateWashProject();
  const canUpdate = canUpdateWashProject();
  const canDelete = canDeleteWashProject();

  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchList(appliedFilters);
  }, [appliedFilters]);

  const fetchList = (filterPayload = {}) => {
    setLoading(true);
    const body = {};
    Object.entries(filterPayload).forEach(([key, value]) => {
      if (value === "" || value == null) return;
      if (key === "status") {
        const n = Number(value);
        if (!Number.isNaN(n)) body.status = n;
        return;
      }
      body[key] = value;
    });

    listWashProjectsApi(body)
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setRows(Array.isArray(data) ? data : []);
        } else {
          setRows([]);
          cogoToast.error(res.data?.message || "Failed to load WASH projects", {
            hideAfter: 4,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading WASH projects.",
          { hideAfter: 5 }
        );
      });
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.project,
        row.region,
        row.district,
        row.donor,
        row.site_name,
        row.water_source,
        row.functionality,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleDelete = (row) => {
    if (!canDelete || !row?.id) return;
    Swal.fire({
      title: "Deactivate this WASH project?",
      text: `"${row.project || "This project"}" will be marked inactive.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, deactivate",
      showDenyButton: true,
      denyButtonText: "Hard delete",
      denyButtonColor: "#7f1d1d",
    }).then((result) => {
      if (!result.isConfirmed && !result.isDenied) return;
      const payload = { id: row.id };
      if (result.isDenied) payload.force = true;

      deleteWashProjectApi(payload)
        .then((res) => {
          if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
            cogoToast.success(res.data.message || "Deleted", { hideAfter: 3 });
            fetchList(appliedFilters);
          } else {
            cogoToast.error(res.data?.message || "Delete failed", { hideAfter: 5 });
          }
        })
        .catch((err) => {
          cogoToast.error(
            err?.response?.data?.message || "Something went wrong deleting.",
            { hideAfter: 5 }
          );
        });
    });
  };

  return (
    <div className="container-xl mt-4 mb-5 wash-page">
      <div className="wash-page__header">
        <div>
          <h1 className="wash-page__title">Wash Projects</h1>
          <p className="wash-page__subtitle">
            Browse, filter and manage WASH infrastructure project entries.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button color="secondary" outline onClick={() => fetchList(appliedFilters)}>
            <FaSync className="me-1" /> Refresh
          </Button>
          {canCreate && (
            <Button
              color="primary"
              onClick={() => history.push("/projects/wash/create")}
            >
              <FaPlus className="me-1" /> Create Project Entry
            </Button>
          )}
          {canCreate && (
            <Button
              color="success"
              outline
              onClick={() => history.push("/projects/wash/upload")}
            >
              <FaFileExcel className="me-1" /> Upload From Excel
            </Button>
          )}
          <Button
            color="info"
            outline
            onClick={() => history.push("/projects/wash/dashboard")}
          >
            WASH Dashboard
          </Button>
        </div>
      </div>

      <div className="wash-card mb-3">
        <div className="wash-card__body">
          <div className="wash-filters">
            <FormGroup className="mb-0">
              <Label className="wash-label">Region</Label>
              <Input
                value={filters.region}
                onChange={(e) => setFilters((p) => ({ ...p, region: e.target.value }))}
                placeholder="e.g. Banadir"
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="wash-label">District</Label>
              <Input
                value={filters.district}
                onChange={(e) => setFilters((p) => ({ ...p, district: e.target.value }))}
                placeholder="e.g. Shibis"
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="wash-label">Donor</Label>
              <Input
                value={filters.donor}
                onChange={(e) => setFilters((p) => ({ ...p, donor: e.target.value }))}
                placeholder="e.g. ECHO"
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="wash-label">Status</Label>
              <Input
                type="select"
                value={filters.status}
                onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="">All</option>
                <option value="10001">Active</option>
                <option value="10000">Draft</option>
                <option value="10002">Inactive</option>
              </Input>
            </FormGroup>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button
              color="primary"
              onClick={() => setAppliedFilters({ ...filters })}
            >
              <FaSearch className="me-1" /> Apply filters
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setFilters({ ...EMPTY_FILTERS });
                setAppliedFilters({ ...EMPTY_FILTERS });
                setSearch("");
              }}
            >
              Clear
            </Button>
            <Input
              className="ms-auto"
              style={{ maxWidth: 280 }}
              placeholder="Search loaded results…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="wash-card">
        <div className="wash-card__body">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover className="wash-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Region</th>
                    <th>District</th>
                    <th>Donor</th>
                    <th>Water source</th>
                    <th>Functionality</th>
                    <th>Budget</th>
                    <th>Status</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        No WASH projects found.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="fw-semibold">{formatCell(row.project)}</div>
                          {row.site_name ? (
                            <div className="text-muted small">{row.site_name}</div>
                          ) : null}
                        </td>
                        <td>{formatCell(row.region)}</td>
                        <td>{formatCell(row.district)}</td>
                        <td>{formatCell(row.donor)}</td>
                        <td>{formatCell(row.water_source)}</td>
                        <td>{formatCell(row.functionality)}</td>
                        <td>{formatMoney(row.budget_value)}</td>
                        <td>
                          <Badge color={washStatusColor(row.status)}>
                            {washStatusLabel(row.status)}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              color="info"
                              outline
                              size="sm"
                              tag={Link}
                              to={`/projects/wash/details/${row.id}`}
                              title="View"
                            >
                              <FaEye />
                            </Button>
                            {canUpdate && (
                              <Button
                                color="primary"
                                outline
                                size="sm"
                                tag={Link}
                                to={`/projects/wash/edit/${row.id}`}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                onClick={() => handleDelete(row)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && (
            <div className="text-muted small mt-2">
              Showing {filteredRows.length} of {rows.length} project(s)
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WashProjectsList;
